import { useMemo, useState, type ReactNode } from "react";
import { Modal } from "./Modal";
import { formatDateCs, MEAL_TYPE_LABELS } from "../lib/utils";
import { formatRecipeLastUsed, sortRecipes, type RecipeSortBy } from "../lib/recipeSort";
import type { Ingredient, MealPlanEntry, Recipe } from "../types";

export type SlotSavePayload = {
  date: string;
  mealType: string;
  servings: number;
  notes?: string;
  recipeId: string | null;
  customTitle?: string | null;
  customIngredients?: { ingredientId: string; quantity: number }[];
};

type TabId = "recipe" | "custom";

type CustomRow = { ingredientId: string; quantity: number | "" };

function isCustomEntry(entry?: MealPlanEntry): boolean {
  if (!entry) return false;
  return !entry.recipeId && !!(entry.customTitle || (entry.customIngredients?.length ?? 0) > 0);
}

export function MealSlotEditor({
  slot,
  recipes,
  ingredients,
  defaultServings,
  onClose,
  onSave,
  onRemove,
}: {
  slot: { date: string; mealType: string; entry?: MealPlanEntry };
  recipes: Recipe[];
  ingredients: Ingredient[];
  defaultServings: number;
  onClose: () => void;
  onSave: (payload: SlotSavePayload) => void;
  onRemove: () => void;
}) {
  const [tab, setTab] = useState<TabId>(() => (isCustomEntry(slot.entry) ? "custom" : "recipe"));
  const [recipeId, setRecipeId] = useState(slot.entry?.recipeId ?? "");
  const [servings, setServings] = useState(slot.entry?.servings ?? defaultServings);
  const [notes, setNotes] = useState(slot.entry?.notes ?? "");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<RecipeSortBy>(() => {
    try {
      const v = localStorage.getItem("jedalnicek:recipe-pick-sort");
      if (v === "name" || v === "lastUsedAsc" || v === "lastUsedDesc") return v;
    } catch {
      /* ignore */
    }
    return "lastUsedAsc";
  });
  const [customTitle, setCustomTitle] = useState(slot.entry?.customTitle ?? "");
  const [customRows, setCustomRows] = useState<CustomRow[]>(() => {
    const existing = slot.entry?.customIngredients ?? [];
    if (existing.length > 0) {
      return existing.map((ci) => ({ ingredientId: ci.ingredientId, quantity: ci.quantity }));
    }
    return [{ ingredientId: "", quantity: "" }];
  });

  const label = MEAL_TYPE_LABELS[slot.mealType] ?? slot.mealType;

  const handleSortChange = (v: RecipeSortBy) => {
    setSortBy(v);
    try {
      localStorage.setItem("jedalnicek:recipe-pick-sort", v);
    } catch {
      /* ignore */
    }
  };

  const canSaveRecipe = !!recipeId;
  const canSaveCustom =
    !!customTitle.trim() &&
    customRows.some((r) => r.ingredientId && typeof r.quantity === "number" && r.quantity > 0);

  const handleSave = () => {
    if (tab === "recipe") {
      if (!canSaveRecipe) return;
      onSave({
        date: slot.date,
        mealType: slot.mealType,
        recipeId,
        servings,
        notes,
        customTitle: null,
        customIngredients: [],
      });
      return;
    }
    if (!canSaveCustom) return;
    onSave({
      date: slot.date,
      mealType: slot.mealType,
      recipeId: null,
      servings,
      notes,
      customTitle: customTitle.trim(),
      customIngredients: customRows
        .filter((r) => r.ingredientId && typeof r.quantity === "number" && r.quantity > 0)
        .map((r) => ({ ingredientId: r.ingredientId, quantity: r.quantity as number })),
    });
  };

  return (
    <Modal
      open
      wide="xl"
      onClose={onClose}
      title={`${label} · ${formatDateCs(slot.date)}`}
      footer={
        <>
          {slot.entry && (
            <button className="btn-danger mr-auto flex items-center gap-1" onClick={onRemove}>
              <span aria-hidden>🗑️</span>
              <span>Odebrat</span>
            </button>
          )}
          <button className="btn-ghost" onClick={onClose}>
            Zrušit
          </button>
          <button
            className="btn-primary flex items-center gap-1"
            disabled={tab === "recipe" ? !canSaveRecipe : !canSaveCustom}
            onClick={handleSave}
          >
            <span aria-hidden>💾</span>
            <span>Uložit</span>
          </button>
        </>
      }
    >
      <div className="flex gap-1 border-b border-slate-200 mb-4">
        <TabButton active={tab === "recipe"} onClick={() => setTab("recipe")}>
          Z receptu
        </TabButton>
        <TabButton active={tab === "custom"} onClick={() => setTab("custom")}>
          Vlastní
        </TabButton>
      </div>

      {tab === "recipe" ? (
        <RecipeTab
          mealType={slot.mealType}
          recipes={recipes}
          recipeId={recipeId}
          onRecipeSelect={setRecipeId}
          search={search}
          onSearchChange={setSearch}
          servings={servings}
          onServingsChange={setServings}
          notes={notes}
          onNotesChange={setNotes}
          sortBy={sortBy}
          onSortChange={handleSortChange}
        />
      ) : (
        <CustomTab
          customTitle={customTitle}
          onCustomTitleChange={setCustomTitle}
          customRows={customRows}
          onCustomRowsChange={setCustomRows}
          ingredients={ingredients}
          notes={notes}
          onNotesChange={setNotes}
        />
      )}
    </Modal>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors " +
        (active
          ? "border-brand-600 text-brand-700"
          : "border-transparent text-slate-500 hover:text-slate-700")
      }
    >
      {children}
    </button>
  );
}

function ServingsStepper({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        className="btn-secondary px-2.5 py-1 min-w-[2rem]"
        onClick={() => onChange(Math.max(0.5, Math.round((value - 0.5) * 2) / 2))}
        aria-label="Méně porcí"
      >
        −
      </button>
      <span className="w-10 text-center font-medium tabular-nums">{value}</span>
      <button
        type="button"
        className="btn-secondary px-2.5 py-1 min-w-[2rem]"
        onClick={() => onChange(Math.round((value + 0.5) * 2) / 2)}
        aria-label="Více porcí"
      >
        +
      </button>
      <span className="text-xs text-slate-500 ml-1">porcí</span>
    </div>
  );
}

function RecipeTab({
  mealType,
  recipes,
  recipeId,
  onRecipeSelect,
  search,
  onSearchChange,
  servings,
  onServingsChange,
  notes,
  onNotesChange,
  sortBy,
  onSortChange,
}: {
  mealType: string;
  recipes: Recipe[];
  recipeId: string;
  onRecipeSelect: (id: string) => void;
  search: string;
  onSearchChange: (v: string) => void;
  servings: number;
  onServingsChange: (n: number) => void;
  notes: string;
  onNotesChange: (v: string) => void;
  sortBy: RecipeSortBy;
  onSortChange: (v: RecipeSortBy) => void;
}) {
  const matchLabel = MEAL_TYPE_LABELS[mealType] ?? mealType;
  const q = search.trim().toLocaleLowerCase("cs");

  const { suitable, others } = useMemo(() => {
    const suitable: Recipe[] = [];
    const others: Recipe[] = [];
    for (const r of recipes) {
      if (q && !r.name.toLocaleLowerCase("cs").includes(q)) continue;
      const mts = r.mealTypes ?? [];
      if (mts.length === 0 || mts.includes(mealType)) suitable.push(r);
      else others.push(r);
    }
    return {
      suitable: sortRecipes(suitable, sortBy),
      others: sortRecipes(others, sortBy),
    };
  }, [recipes, mealType, q, sortBy]);

  const showLastUsed = sortBy !== "name";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="label" htmlFor="recipe-search">
            Vyhledat recept
          </label>
          <input
            id="recipe-search"
            className="input"
            placeholder="Název receptu…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            autoFocus
          />
        </div>
        <div>
          <span className="label block">Porce</span>
          <ServingsStepper value={servings} onChange={onServingsChange} />
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="label" htmlFor="slot-notes-recipe">
            Poznámka
          </label>
          <input
            id="slot-notes-recipe"
            className="input"
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder="Volitelné…"
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-slate-500">
          Klikněte na řádek pro výběr receptu. Zelené = vhodné pro {matchLabel}, oranžové = jiný chod.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <label className="text-xs text-slate-500 whitespace-nowrap" htmlFor="recipe-table-sort">
            Řadit:
          </label>
          <select
            id="recipe-table-sort"
            className="input text-sm py-1 max-w-[180px]"
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value as RecipeSortBy)}
          >
            <option value="lastUsedAsc">Nejdéle nepoužité</option>
            <option value="lastUsedDesc">Naposledy použité</option>
            <option value="name">Podle názvu</option>
          </select>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden max-h-[320px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 font-medium text-slate-600">Recept</th>
              <th className="px-3 py-2 font-medium text-slate-600 w-36">Vhodnost</th>
              {showLastUsed && (
                <th className="px-3 py-2 font-medium text-slate-600 w-32">Naposledy</th>
              )}
            </tr>
          </thead>
          <tbody>
            {suitable.length === 0 && others.length === 0 ? (
              <tr>
                <td colSpan={showLastUsed ? 3 : 2} className="px-3 py-6 text-center text-slate-500">
                  {q ? "Žádný recept neodpovídá hledání." : "Zatím nemáte žádné recepty v katalogu."}
                </td>
              </tr>
            ) : (
              <>
                {suitable.length > 0 && (
                  <RecipeGroup
                    title={`Vhodné pro ${matchLabel}`}
                    recipes={suitable}
                    mealType={mealType}
                    selectedId={recipeId}
                    onSelect={onRecipeSelect}
                    suitable
                    showLastUsed={showLastUsed}
                  />
                )}
                {others.length > 0 && (
                  <RecipeGroup
                    title="Jiné recepty"
                    recipes={others}
                    mealType={mealType}
                    selectedId={recipeId}
                    onSelect={onRecipeSelect}
                    suitable={false}
                    showLastUsed={showLastUsed}
                  />
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RecipeGroup({
  title,
  recipes,
  mealType,
  selectedId,
  onSelect,
  suitable,
  showLastUsed,
}: {
  title: string;
  recipes: Recipe[];
  mealType: string;
  selectedId: string;
  onSelect: (id: string) => void;
  suitable: boolean;
  showLastUsed: boolean;
}) {
  const rowBg = suitable
    ? "bg-emerald-50 hover:bg-emerald-100/80"
    : "bg-amber-50 hover:bg-amber-100/80";
  const selectedRing = "ring-2 ring-inset ring-brand-500";

  return (
    <>
      <tr className="bg-slate-100">
        <td
          colSpan={showLastUsed ? 3 : 2}
          className="px-3 py-1.5 text-xs font-semibold text-slate-600 uppercase tracking-wide"
        >
          {title}
        </td>
      </tr>
      {recipes.map((r) => {
        const mts = r.mealTypes ?? [];
        const universal = mts.length === 0;
        const suitability = universal
          ? "Univerzální"
          : suitable
            ? MEAL_TYPE_LABELS[mealType] ?? mealType
            : mts.map((m) => MEAL_TYPE_LABELS[m] ?? m).join(", ");

        return (
          <tr
            key={r.id}
            className={`cursor-pointer border-t border-white/60 ${rowBg} ${selectedId === r.id ? selectedRing : ""}`}
            onClick={() => onSelect(r.id)}
          >
            <td className="px-3 py-2 font-medium text-slate-900">{r.name}</td>
            <td className="px-3 py-2 text-xs text-slate-600">{suitability}</td>
            {showLastUsed && (
              <td className="px-3 py-2 text-xs text-slate-500">
                {formatRecipeLastUsed(r.lastUsedInMealPlanAt)}
              </td>
            )}
          </tr>
        );
      })}
    </>
  );
}

function CustomTab({
  customTitle,
  onCustomTitleChange,
  customRows,
  onCustomRowsChange,
  ingredients,
  notes,
  onNotesChange,
}: {
  customTitle: string;
  onCustomTitleChange: (v: string) => void;
  customRows: CustomRow[];
  onCustomRowsChange: (rows: CustomRow[]) => void;
  ingredients: Ingredient[];
  notes: string;
  onNotesChange: (v: string) => void;
}) {
  const sortedIngredients = useMemo(
    () => [...ingredients].sort((a, b) => a.name.localeCompare(b.name, "cs")),
    [ingredients]
  );

  const updateRow = (index: number, patch: Partial<CustomRow>) => {
    onCustomRowsChange(customRows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const addRow = () => onCustomRowsChange([...customRows, { ingredientId: "", quantity: "" }]);

  const removeRow = (index: number) => {
    if (customRows.length <= 1) {
      onCustomRowsChange([{ ingredientId: "", quantity: "" }]);
      return;
    }
    onCustomRowsChange(customRows.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Vlastní jídlo bez receptu — např. grilování, párty nebo jednorázové menu. Suroviny se započítají do
        nákupního seznamu.
      </p>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="label" htmlFor="custom-title">
            Název jídla
          </label>
          <input
            id="custom-title"
            className="input"
            value={customTitle}
            onChange={(e) => onCustomTitleChange(e.target.value)}
            placeholder="např. Grilování"
            autoFocus
          />
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="label" htmlFor="slot-notes-custom">
            Poznámka
          </label>
          <input
            id="slot-notes-custom"
            className="input"
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder="Volitelné…"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="label mb-0">Suroviny</h3>
          <button type="button" className="btn-secondary text-xs" onClick={addRow}>
            + Přidat surovinu
          </button>
        </div>

        {sortedIngredients.length === 0 ? (
          <p className="text-sm text-slate-500 rounded-lg border border-dashed p-4">
            Nejprve si vytvořte suroviny v katalogu.
          </p>
        ) : (
          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {customRows.map((row, index) => {
              const ing = ingredients.find((i) => i.id === row.ingredientId);
              return (
                <div key={index} className="flex flex-wrap items-center gap-2">
                  <select
                    className="input flex-1 min-w-[180px]"
                    value={row.ingredientId}
                    onChange={(e) => updateRow(index, { ingredientId: e.target.value })}
                  >
                    <option value="">— vyberte surovinu —</option>
                    {sortedIngredients.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name} ({i.unit})
                      </option>
                    ))}
                  </select>
                  <input
                    className="input w-28"
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="Množství"
                    value={row.quantity}
                    onChange={(e) =>
                      updateRow(index, {
                        quantity: e.target.value === "" ? "" : Number(e.target.value),
                      })
                    }
                  />
                  <span className="text-sm text-slate-500 w-8">{ing?.unit ?? ""}</span>
                  <button
                    type="button"
                    className="btn-ghost text-xs text-red-600"
                    onClick={() => removeRow(index)}
                    title="Odebrat řádek"
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
